/**
 * Generic CSV export utility.
 *
 * Converts an array of objects into RFC 4180-compliant CSV, creates a Blob,
 * triggers a browser download, then revokes the object URL.
 *
 * @example
 * ```ts
 * exportToCsv({
 *   filename: "acme-roster-2026-04-13.csv",
 *   columns: [
 *     { header: "First Name", accessor: (r) => r.firstName ?? "" },
 *     { header: "Email", accessor: (r) => r.workEmail },
 *   ],
 *   rows: rosterEntries,
 * });
 * ```
 */

export interface CsvColumn<T> {
  /** Column header label in the CSV */
  header: string;
  /** Extracts the cell value from a row object */
  accessor: (row: T) => string | number;
}

interface ExportToCsvOptions<T> {
  filename: string;
  columns: CsvColumn<T>[];
  rows: T[];
}

/**
 * Escapes a CSV cell value per RFC 4180:
 * - Wraps in double-quotes if the value contains commas, double-quotes, or newlines
 * - Escapes internal double-quotes by doubling them
 */
function escapeCsvCell(value: string | number): string {
  const str = String(value);
  if (str.includes(",") || str.includes('"') || str.includes("\n") || str.includes("\r")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

export function exportToCsv<T>({ filename, columns, rows }: ExportToCsvOptions<T>): void {
  // Build header row
  const headerLine = columns.map((col) => escapeCsvCell(col.header)).join(",");

  // Build data rows
  const dataLines = rows.map((row) =>
    columns.map((col) => escapeCsvCell(col.accessor(row))).join(","),
  );

  // BOM prefix for Excel UTF-8 compatibility (important for German characters: ä, ö, ü, ß)
  const BOM = "\uFEFF";
  const csvContent = BOM + [headerLine, ...dataLines].join("\r\n");

  // Create blob and trigger download
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Generates a standardized export filename.
 * Format: `{prefix}-{YYYY-MM-DD}.csv`
 */
export function buildExportFilename(orgSlug: string, dataType: string): string {
  const date = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
  const safeName = orgSlug.replace(/[^a-z0-9-]/gi, "-").toLowerCase();
  return `${safeName}-${dataType}-${date}.csv`;
}
